// Path: src/pages/Login.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, User, Eye, EyeOff, Camera, RefreshCw, SwitchCamera, Scan, Video, Sun, SunDim, AlertTriangle } from 'lucide-react';
import { getBgVideoUrls } from '../lib/api/bgVideo';
import { getCities, loginFaceWithMetadata, parseFaceError } from '../lib/api/faceV2';
import type { FaceCity } from '../lib/api/faceV2';
import {
  getBestRecordingMimeType,
  getRecordingDebugMeta,
  buildFaceRecordUploadPayload,
  validateRecordingDuration,
  logRecordingDebugInfo,
  parseRecordingError,
  RECORDING_ERRORS,
} from '../lib/utils/videoRecording';
import type { RecordingMetadata } from '../lib/utils/videoRecording';
import logo from '../assets/imgs/logo.png';

const STORAGE_KEY_VIDEO_INDEX = 'cyber_edu_video_index';
const FACE_SCAN_TIMEOUT = 180000; // 摄像头倒计时超时：180秒（3分钟）
const BG_VIDEO_SYS = 'aitcyc';
const BG_VIDEO_PAGE = 'login';
const ENABLE_FACE_LOGIN = true;
const MIN_RECORDING_DURATION_MS = 3000;
const DEFAULT_RECORDING_DURATION = 5;

type LoginMode = 'face' | 'password';

export const Login: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loginMode, setLoginMode] = useState<LoginMode>('password');
  
  const [videoUrls, setVideoUrls] = useState<string[]>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState<boolean>(true);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string>('');
  const [scanCountdown, setScanCountdown] = useState<number>(FACE_SCAN_TIMEOUT / 1000);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const faceVideoRef = useRef<HTMLVideoElement>(null);
  const faceCanvasRef = useRef<HTMLCanvasElement>(null);
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const [cities, setCities] = useState<FaceCity[]>([]);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [capturedImage, setCapturedImage] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  
  // 亮度增强相关状态
  const [brightness, setBrightness] = useState(120);
  const [showFlash, setShowFlash] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  
  // 录制格式相关状态
  const [requestedMimeType, setRequestedMimeType] = useState('');
  const [actualMimeType, setActualMimeType] = useState('');
  const [recordingStartTime, setRecordingStartTime] = useState<number>(0);
  const [videoWidth, setVideoWidth] = useState(640);
  const [videoHeight, setVideoHeight] = useState(480);
  
  const navigate = useNavigate();
  const { login, fetchUser } = useAuth();

  useEffect(() => {
    const fetchVideoUrls = async () => {
      setIsLoadingVideos(true);
      try {
        const response = await getBgVideoUrls(BG_VIDEO_SYS, BG_VIDEO_PAGE);
        const urls = response.data;
        if (urls && urls.length > 0) {
          setVideoUrls(urls);
          
          try {
            const savedIndex = localStorage.getItem(STORAGE_KEY_VIDEO_INDEX);
            if (savedIndex !== null) {
              const parsed = parseInt(savedIndex, 10);
              if (!isNaN(parsed) && parsed >= 0 && parsed < urls.length) {
                setCurrentIndex(parsed);
              }
            }
          } catch (e) {
            console.warn('LocalStorage access failed', e);
          }
        }
      } catch (err) {
        console.error('Failed to fetch video URLs:', err);
      } finally {
        setIsLoadingVideos(false);
      }
    };
    
    fetchVideoUrls();
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await getCities();
        setCities(response.data);
      } catch (err) {
        console.error('Failed to fetch cities:', err);
      }
    };
    fetchCities();
  }, []);

  const handleCanPlay = () => {
    setIsVideoReady(true);
  };

  const handleVideoEnded = useCallback(() => {
    if (videoUrls.length === 0) return;
    setIsVideoReady(false);
    
    if (videoUrls.length === 1) {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(e => console.log('Auto-play prevented:', e));
      }
    } else {
      setCurrentIndex(prev => {
        const nextIndex = (prev + 1) % videoUrls.length;
        try {
          localStorage.setItem(STORAGE_KEY_VIDEO_INDEX, nextIndex.toString());
        } catch (e) {}
        return nextIndex;
      });
    }
  }, [videoUrls.length]);

  useEffect(() => {
    if (videoRef.current && videoUrls.length > 0) {
      setIsVideoReady(false);
      videoRef.current.load();
      videoRef.current.play().catch(e => console.log('Auto-play prevented:', e));
    }
  }, [currentIndex, videoUrls]);

  const stopCamera = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setCameraActive(false);
    setIsScanning(false);
    setIsRecording(false);
    setRecordedVideo(null);
    setCapturedImage(null);
    setRecordingTime(0);
  }, []);

  const startRecording = useCallback(() => {
    if (!cameraStreamRef.current) return;
    
    // 探测最佳录制格式
    const capability = getBestRecordingMimeType();
    const mimeType = capability.requestedMimeType;
    
    setRequestedMimeType(mimeType);
    
    console.log('[登录录制能力探测]', {
      支持的格式: capability.supportedMimeTypes,
      选择的格式: mimeType
    });
    
    let mediaRecorder: MediaRecorder;
    
    try {
      if (mimeType && MediaRecorder.isTypeSupported(mimeType)) {
        mediaRecorder = new MediaRecorder(cameraStreamRef.current, { mimeType });
      } else {
        mediaRecorder = new MediaRecorder(cameraStreamRef.current);
      }
    } catch (err) {
      console.error('MediaRecorder creation failed:', err);
      setCameraError(RECORDING_ERRORS.RECORDING_START_FAILED);
      return;
    }
    
    // 记录实际使用的 mimeType
    const actualMime = mediaRecorder.mimeType;
    setActualMimeType(actualMime);
    
    console.log('[登录实际录制格式]', actualMime);
    
    const chunks: Blob[] = [];
    
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };
    
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: actualMime });
      setRecordedVideo(blob);
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      // 输出调试信息 
      logRecordingDebugInfo({
        supportedMimeTypes: capability.supportedMimeTypes,
        requestedMimeType: mimeType,
        actualMimeType: actualMime,
        blobType: blob.type,
        fileName: `face_record.${actualMime.includes('mp4') ? 'mp4' : 'webm'}`,
        fileSize: blob.size,
        durationMs: Date.now() - recordingStartTime,
      });
    };
    
    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);
    setRecordingTime(0);
    setRecordingStartTime(Date.now());
    
    recordingIntervalRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= DEFAULT_RECORDING_DURATION - 1) {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
          return DEFAULT_RECORDING_DURATION;
        }
        return prev + 1;
      });
    }, 1000);
  }, [recordingStartTime]);

  const captureImage = useCallback(() => {
    if (!faceVideoRef.current || !faceCanvasRef.current) return;
    
    setIsEnhancing(true);
    
    const video = faceVideoRef.current;
    const canvas = faceCanvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // 触发屏幕闪光效果
    setShowFlash(true);
    flashTimeoutRef.current = setTimeout(() => {
      setShowFlash(false);
    }, 150);
    
    // 绘制原始图像
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // 应用亮度增强
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const factor = brightness / 100;
    
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.min(255, data[i] * factor);
      data[i + 1] = Math.min(255, data[i + 1] * factor);
      data[i + 2] = Math.min(255, data[i + 2] * factor);
    }
    
    context.putImageData(imageData, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedImage(blob);
      }
      setIsEnhancing(false);
    }, 'image/jpeg', 0.9);
  }, [brightness]);

  const startCamera = useCallback(async () => {
    setCameraError('');
    setScanCountdown(FACE_SCAN_TIMEOUT / 1000);
    setRecordedVideo(null);
    setCapturedImage(null);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      
      cameraStreamRef.current = stream;
      
      const videoTrack = stream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      setVideoWidth(settings.width || 640);
      setVideoHeight(settings.height || 480);
      
      setCameraActive(true);
      
      setTimeout(() => {
        if (faceVideoRef.current && cameraStreamRef.current) {
          faceVideoRef.current.srcObject = cameraStreamRef.current;
          faceVideoRef.current.play().catch(e => {
            console.error('Video play error:', e);
          });
        }
      }, 100);
      
      countdownIntervalRef.current = setInterval(() => {
        setScanCountdown(prev => {
          if (prev <= 1) {
            setError('人脸扫描超时，请尝试用户名密码登录');
            stopCamera();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err: any) {
      console.error('Camera access error:', err);
      setCameraError('无法访问摄像头，请检查权限设置');
      setCameraActive(false);
    }
  }, [stopCamera]);

  useEffect(() => {
    if (loginMode === 'face') {
      startCamera();
    } else {
      stopCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [loginMode, startCamera, stopCamera]);

  const captureAndLogin = async () => {
    if (!selectedCity) {
      setError('请先选择城市');
      return;
    }
    
    if (!recordedVideo) {
      setError('请先录制视频（点击"开始录制"按钮）');
      return;
    }
    
    if (!capturedImage) {
      setError('请先拍摄人脸照片');
      return;
    }
    
    // 校验录制时长
    const durationMs = Date.now() - recordingStartTime;
    const validation = validateRecordingDuration(durationMs, MIN_RECORDING_DURATION_MS);
    
    if (!validation.valid) {
      setError(validation.message);
      return;
    }
    
    setIsScanning(true);
    setError('');
    
    try {
      // 构建录制元数据
      const metadata: RecordingMetadata = getRecordingDebugMeta({
        requestedMimeType,
        actualMimeType,
        durationMs,
        videoWidth,
        videoHeight,
        facingMode: 'user',
      });
      
      // 构建上传载荷
      const formData = buildFaceRecordUploadPayload(recordedVideo, capturedImage, metadata);
      
      console.log('[登录上传元数据]', metadata);
      
      const response = await loginFaceWithMetadata(selectedCity, formData);
      
      // 服务端直接返回 access_token 和用户信息
      if (response.data.access_token) {
        const token = response.data.access_token;
        const userData = response.data;
        
        console.log('[人脸登录成功] 用户信息:', userData);
        
        // 存储 token（与密码登录一致）
        if (rememberMe) {
          const now = new Date();
          const item = {
            value: token,
            expiry: now.getTime() + 15 * 24 * 60 * 60 * 1000
          };
          localStorage.setItem('token', JSON.stringify(item));
        } else {
          sessionStorage.setItem('token', token);
        }
        
        // 关闭摄像头
        stopCamera();
        
        // 更新 AuthContext 的用户状态（关键：传入显式 token！）
        await fetchUser(token);
        
        // 根据角色跳转到不同页面
        if (userData.role === 'sys' || userData.role === 'admin') {
          navigate('/admin');
        } else if (userData.role === 'teacher') {
          navigate('/dashboard');
        } else {
          navigate('/');
        }
      } else {
        setError('人脸识别失败，未返回访问令牌');
      }
    } catch (err: any) {
      const errorMsg = parseFaceError(err);
      setError(errorMsg);
    } finally {
      setIsScanning(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    if (!username.trim() || !password.trim()) {
      setError('凭证缺失');
      setIsLoading(false);
      return;
    }
    
    try {
      const loggedInUser = await login(username, password, rememberMe);
      if (loggedInUser) {
        // 根据角色跳转到不同页面
        if (loggedInUser.role === 'sys' || loggedInUser.role === 'admin') {
          navigate('/admin');
        } else if (loggedInUser.role === 'teacher') {
          navigate('/dashboard');
        } else {
          navigate('/');
        }
      } else {
        setError('身份不匹配');
        setIsLoading(false);
      }
    } catch (err: any) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      
      if (status === 403 && (detail.includes('password_login') || detail.includes('密码登录'))) {
        setError('密码登录已禁用，请使用刷脸登录');
      } else {
        setError(detail || err.message || '网络断开');
      }
      setIsLoading(false);
    }
  };

  const toggleLoginMode = () => {
    setError('');
    setLoginMode(prev => prev === 'face' ? 'password' : 'face');
  };

  const getCurrentMedia = useCallback(() => {
    if (videoUrls.length === 0 || currentIndex >= videoUrls.length) {
      return { video: '', image: '' };
    }
    
    const currentUrl = videoUrls[currentIndex];
    const videoUrl = currentUrl.replace('.png', '.mp4').replace('.jpg', '.mp4');
    
    return {
      video: videoUrl,
      image: currentUrl
    };
  }, [videoUrls, currentIndex]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const currentMedia = getCurrentMedia();
  const totalVideos = videoUrls.length;

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-black">
      {/* 屏幕闪光效果 */}
      {showFlash && (
        <div 
          className="fixed inset-0 bg-white z-[100] pointer-events-none"
          style={{ 
            animation: 'flash 0.15s ease-out forwards',
            opacity: 0.8
          }}
        />
      )}
      
      {!isLoadingVideos && totalVideos > 0 && (
        <>
          <div 
            className={`absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out ${
              isVideoReady ? 'opacity-0' : 'opacity-100'
            }`}
            style={{ backgroundImage: `url(${currentMedia.image})` }}
          ></div>

          <video
            ref={videoRef}
            muted
            playsInline
            onCanPlay={handleCanPlay}
            onEnded={handleVideoEnded}
            className={`absolute inset-0 z-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
              isVideoReady ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={currentMedia.video} type="video/mp4" />
          </video>
        </>
      )}

      <div className="absolute inset-0 z-0 bg-black/20 pointer-events-none"></div>

      <div className="absolute top-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center">
        <div className="w-16 h-16 sm:w-20 sm:h-20 relative">
          <div className="absolute inset-0 bg-cyan-400 opacity-20 blur-xl rounded-full animate-pulse"></div>
          <img src={logo} alt="Logo" className="relative z-10 w-full h-full object-contain filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
        </div>
        <h1 className="mt-2 text-xl font-bold text-white/90 tracking-[0.2em] uppercase text-shadow-sm">
          Neural Nexus
        </h1>
      </div>

      <div className="relative z-10 w-[320px] sm:w-[360px] mt-12">
        <div className="backdrop-blur-md bg-black/30 border border-white/5 p-6 rounded-xl shadow-2xl">
          
          {loginMode === 'face' ? (
            <div className="space-y-4">
              {error && (
                <div className="bg-red-500/20 text-red-300 px-3 py-2 rounded text-xs text-center backdrop-blur-sm animate-pulse">
                  {error}
                </div>
              )}
              
              {cameraError && (
                <div className="bg-yellow-500/20 text-yellow-300 px-3 py-2 rounded text-xs text-center backdrop-blur-sm">
                  {cameraError}
                </div>
              )}
              
              {/* 步骤指示器 */}
              <div className="flex items-center justify-center gap-2 text-xs">
                <div className={`flex items-center gap-1 ${selectedCity ? 'text-green-400' : 'text-cyan-400'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${selectedCity ? 'bg-green-400/20' : 'bg-cyan-400/20'}`}>
                    {selectedCity ? '✓' : '1'}
                  </span>
                  <span>选城市</span>
                </div>
                <span className="text-white/30">→</span>
                <div className={`flex items-center gap-1 ${recordedVideo ? 'text-green-400' : !selectedCity ? 'text-white/30' : 'text-cyan-400'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${recordedVideo ? 'bg-green-400/20' : !selectedCity ? 'bg-white/10' : 'bg-cyan-400/20'}`}>
                    {recordedVideo ? '✓' : '2'}
                  </span>
                  <span>录视频</span>
                </div>
                <span className="text-white/30">→</span>
                <div className={`flex items-center gap-1 ${capturedImage ? 'text-green-400' : !recordedVideo ? 'text-white/30' : 'text-cyan-400'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${capturedImage ? 'bg-green-400/20' : !recordedVideo ? 'bg-white/10' : 'bg-cyan-400/20'}`}>
                    {capturedImage ? '✓' : '3'}
                  </span>
                  <span>拍照片</span>
                </div>
                <span className="text-white/30">→</span>
                <div className={`flex items-center gap-1 ${recordedVideo && capturedImage ? 'text-green-400' : 'text-white/30'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${recordedVideo && capturedImage ? 'bg-green-400/20' : 'bg-white/10'}`}>
                    4
                  </span>
                  <span>登录</span>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  选择城市 {!selectedCity && <span className="text-cyan-400">*</span>}
                </label>
                <select
                  value={selectedCity || ''}
                  onChange={(e) => setSelectedCity(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:bg-white/10 focus:border-cyan-400/50 transition-all"
                  disabled={isScanning || isRecording}
                >
                  <option value="" className="bg-gray-900">请选择城市</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id} className="bg-gray-900">
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* 亮度调节控制 */}
              {cameraActive && (
                <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs text-amber-400">
                      <Sun size={14} />
                      <span className="font-medium">画面亮度增强</span>
                    </div>
                    <span className="text-xs font-mono text-amber-400/70">{brightness}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SunDim size={14} className="text-amber-400/50" />
                    <input
                      type="range"
                      min="80"
                      max="180"
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                      className="flex-1 h-2 bg-amber-500/20 rounded-lg appearance-none cursor-pointer accent-amber-500"
                    />
                    <Sun size={14} className="text-amber-400" />
                  </div>
                </div>
              )}
              
              <div className="relative aspect-[4/3] bg-black/50 rounded-lg overflow-hidden border border-white/10">
                {cameraActive ? (
                  <>
                    <video
                      ref={faceVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover transform scale-x-[-1]"
                      style={{ filter: `brightness(${brightness}%) contrast(${Math.min(110, brightness * 0.9)}%)` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className={`w-48 h-48 border-2 rounded-full ${isRecording ? 'border-red-400 animate-pulse' : 'border-cyan-400/50'}`}></div>
                    </div>
                    {isRecording && (
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 text-xs text-red-400 bg-black/50 px-2 py-1 rounded">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        录制中: {recordingTime}s / {DEFAULT_RECORDING_DURATION}s
                      </div>
                    )}
                    {recordedVideo && !isRecording && (
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-green-400 bg-black/50 px-2 py-1 rounded">
                        ✓ 视频已录制
                      </div>
                    )}
                    {capturedImage && (
                      <div className="absolute bottom-2 right-2 text-xs text-green-400 bg-black/50 px-2 py-1 rounded">
                        ✓ 照片已拍摄
                      </div>
                    )}
                    {isEnhancing && (
                      <div className="absolute inset-0 bg-white/30 flex items-center justify-center">
                        <RefreshCw size={24} className="animate-spin text-cyan-500" />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white/50">
                    <Camera size={48} className="mb-2 opacity-50" />
                    <span className="text-sm">正在启动摄像头...</span>
                  </div>
                )}
                <canvas ref={faceCanvasRef} className="hidden" />
              </div>
              
              <div className="flex items-center pt-1">
                <div className="flex items-center">
                  <input
                    id="remember-me-face"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-3.5 w-3.5 bg-white/10 border-white/20 rounded text-cyan-500 focus:ring-cyan-500 focus:ring-offset-gray-900 cursor-pointer"
                    disabled={isScanning || isRecording}
                  />
                  <label htmlFor="remember-me-face" className="ml-2 block text-xs text-white/50 cursor-pointer hover:text-white/80 transition-colors">
                    Remember Me
                  </label>
                </div>
              </div>
              
              <div className="space-y-2">
                {/* 步骤1: 录制视频 */}
                {cameraActive && !recordedVideo && (
                  <button
                    onClick={isRecording ? undefined : startRecording}
                    disabled={!cameraActive || isScanning || isRecording || !selectedCity}
                    className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-cyan-400/30 text-xs font-bold uppercase tracking-widest rounded-lg text-cyan-400 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-300 disabled:opacity-50"
                  >
                    {isRecording ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" />
                        录制中... {recordingTime}s
                      </>
                    ) : (
                      <>
                        <Video size={14} />
                        步骤2: 开始录制视频 ({DEFAULT_RECORDING_DURATION}秒)
                      </>
                    )}
                  </button>
                )}
                
                {/* 步骤2: 拍摄照片 */}
                {cameraActive && recordedVideo && !capturedImage && (
                  <button
                    onClick={captureImage}
                    disabled={!cameraActive || isScanning || isRecording}
                    className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-cyan-400/30 text-xs font-bold uppercase tracking-widest rounded-lg text-cyan-400 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-300 disabled:opacity-50"
                  >
                    <Camera size={14} />
                    步骤3: 拍摄人脸照片
                  </button>
                )}
                
                {/* 步骤3: 提交登录 */}
                {recordedVideo && capturedImage && !isRecording && (
                  <>
                    <button
                      onClick={() => { setRecordedVideo(null); setCapturedImage(null); }}
                      disabled={isScanning}
                      className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-white/20 text-xs text-white/70 hover:text-white hover:border-white/40 rounded-lg transition-all disabled:opacity-50"
                    >
                      <RefreshCw size={14} />
                      重新录制
                    </button>
                    <button
                      onClick={captureAndLogin}
                      disabled={isScanning || !selectedCity}
                      className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-green-400/30 text-xs font-bold uppercase tracking-widest rounded-lg text-green-400 bg-green-400/10 hover:bg-green-400 hover:text-black focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-300 disabled:opacity-50"
                    >
                      {isScanning ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          验证登录中...
                        </>
                      ) : (
                        <>
                          <Scan size={14} />
                          步骤4: 提交人脸登录
                        </>
                      )}
                    </button>
                  </>
                )}
                
                {ENABLE_FACE_LOGIN && (
                  <button
                    onClick={toggleLoginMode}
                    className="w-full flex justify-center items-center gap-2 py-2 px-4 text-xs text-white/50 hover:text-white/80 transition-colors"
                  >
                    <SwitchCamera size={14} />
                    切换到用户名密码登录
                  </button>
                )}
              </div>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handlePasswordSubmit}>
              {error && (
                <div className="bg-red-500/20 text-red-300 px-3 py-2 rounded text-xs text-center backdrop-blur-sm animate-pulse">
                  {error}
                </div>
              )}
              
              <div className="space-y-3">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={16} className="text-white/40 group-focus-within:text-cyan-400 transition-colors" />
                  </div>
                  <input
                    name="username"
                    type="text"
                    required
                    className="block w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:bg-white/10 focus:border-cyan-400/50 transition-all"
                    placeholder="System ID"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={16} className="text-white/40 group-focus-within:text-cyan-400 transition-colors" />
                  </div>
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    className="block w-full pl-9 pr-10 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:bg-white/10 focus:border-cyan-400/50 transition-all"
                    placeholder="Passcode"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-white/30 hover:text-white/70 focus:outline-none transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center pt-1">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-3.5 w-3.5 bg-white/10 border-white/20 rounded text-cyan-500 focus:ring-cyan-500 focus:ring-offset-gray-900 cursor-pointer"
                    disabled={isLoading}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-xs text-white/50 cursor-pointer hover:text-white/80 transition-colors">
                    Remember Me
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <button
                  type="submit"
                  className="w-full flex justify-center py-2.5 px-4 border border-cyan-400/30 text-xs font-bold uppercase tracking-widest rounded-lg text-cyan-400 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-300 disabled:opacity-50"
                  disabled={isLoading}
                >
                  {isLoading ? 'INITIALIZING...' : 'ENTER'}
                </button>
                
                {ENABLE_FACE_LOGIN && (
                  <button
                    type="button"
                    onClick={toggleLoginMode}
                    className="w-full flex justify-center items-center gap-2 py-2 px-4 text-xs text-white/50 hover:text-white/80 transition-colors"
                  >
                    <Camera size={14} />
                    切换到人脸识别登录
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
      
      <div className="absolute bottom-3 right-4 text-[10px] font-mono text-white/20 pointer-events-none text-right">
        {totalVideos > 0 && (
          <div>SEQ:[{currentIndex + 1}/{totalVideos}]</div>
        )}
      </div>
      
      {/* 内联CSS动画 */}
      <style>{`
        @keyframes flash {
          0% { opacity: 0.8; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default Login;
